export enum OpzioniTipoFattura {
  PARCELLA_PRIVATI = 'Parcella Privati',
  PARCELLA_SDI = 'Parcella SDI',
  NOTA_CREDITO_SDI = 'Nota di Credito SDI',
  NOTA_CREDITO_STS = 'Nota di Credito STS',
}

export enum PaymentType {
  BONIFICO = 'Bonifico',
  CONTANTI = 'Contanti',
  CARTA_DI_PAGAMENTO = 'Carta di Pagamento',
}

export enum TipologiaCassaCode {
  TC09 = 'TC09',
  TC21 = 'TC21',
}

export const TipologiaCassaDescription = {
  [TipologiaCassaCode.TC09]:
    'TC09 Ente nazionale previdenza e assistenza medici (ENPAM)',
  [TipologiaCassaCode.TC21]:
    'TC21 Ente nazionale previdenza e assistenza psicologi (ENPAP)',
};

// This type helps with frontend display
export type TipologiaCassa = keyof typeof TipologiaCassaDescription;

export enum OpzioniNaturaIva {
  N1 = 'N1',
  N2_1 = 'N2.1',
  N2_2 = 'N2.2',
  N4 = 'N4',
}

export interface StsExpenseLine {
  descrizione: string;
  quantita: number; // aCube uses string for quantity and price.
  prezzo: number;
  iva: number;
  /** Set only when `iva` is 0; must be null for taxable rates (10%, 22%, …). */
  natura_iva: OpzioniNaturaIva | null;
  totale: number;
}

/** Document line as returned by GET /invoicing/sts/:id. */
export interface StsExpenseApiLine {
  descrizione: string;
  quantita: number;
  prezzo: number;
  iva: number;
  natura_iva: OpzioniNaturaIva | string | null;
  totale: number;
  is_bollo?: boolean;
}

/** STS expense returned by GET /invoicing/sts/:id (edit / preview). */
export interface StsExpense {
  id?: string;
  tipo: 'Parcella Privati' | OpzioniTipoFattura | '';
  client_id: string;
  cliente?: string;
  riferimento: string;
  progressivo_numero: string;
  progressivo_anno: number;
  data: string;
  pagata: boolean;
  data_pagamento?: string;
  scadenza: string;
  pagamento: PaymentType | '';
  cassa_previdenza: boolean;
  tipologia_cassa: TipologiaCassa | '';
  percentuale_cassa: number;
  cassa_previdenza_iva?: number | null;
  cassa_previdenza_natura_iva?: OpzioniNaturaIva | string | null;
  applica_bollo?: boolean;
  escludi_bollo?: boolean;
  linee_documento: StsExpenseApiLine[];
  imponibile: number;
  totale_cassa_previdenza: number;
  imposta: number;
  bollo: number;
  totale: number;
  doctor_bank_name: string | null;
  doctor_bank_iban: string | null;
  flag_opposizione?: boolean;
}

/** Private client used in the STS expense form autocomplete. */
export interface StsPrivateClient {
  id?: string;
  client_first_name: string;
  client_last_name: string;
}

export interface StsExpensePayload {
  tipo: 'Parcella Privati';
  client_id: string;
  riferimento: string; // descrizione
  progressivo_numero: string;
  progressivo_anno: number;
  data: string; // date in format dd/mm/yyyy
  pagata: boolean;
  scadenza: string; // vista fattura for example
  pagamento: PaymentType;
  cassa_previdenza: boolean;
  tipologia_cassa: TipologiaCassa;
  percentuale_cassa: number;
  cassa_previdenza_iva: number;
  cassa_previdenza_natura_iva: OpzioniNaturaIva | null;
  applica_bollo: boolean;
  escludi_bollo: boolean;
  linee_documento: StsExpenseLine[];
  imponibile: number;
  totale_cassa_previdenza: number;
  imposta: number;
  bollo: number;
  totale: number;
  doctor_bank_name: string | null;
  doctor_bank_iban: string | null;
  flag_opposizione: boolean;
}

/** Form / UI model for issuing an STS credit note. */
export interface StsCreditNotePayload {
  tipo: 'Nota di Credito STS';
  numero_fattura_originale: string;
  progressivo_numero: string;
  importo: number;
}

/** Body accepted by POST /invoicing/sts/:id/send-credit-note. */
export interface StsCreditNoteApiBody {
  num_documento: string;
  importo: number;
}

export function toStsCreditNoteApiBody(
  payload: StsCreditNotePayload
): StsCreditNoteApiBody {
  return {
    num_documento: payload.progressivo_numero,
    importo: payload.importo,
  };
}
